import cx from 'classnames';
import Color, {type ColorInstance} from 'color';
import type {FC} from 'react';
import {useSelector} from 'react-redux';
import {type FiltersStoreState, selectIsFilterActive} from '~/GalleryPB/reducers/filters';
import type {FilterType} from '~/GalleryPB/types/filters';
import {Button} from '~library/atoms/Button';
import {Pill} from '~library/atoms/Pill';
import {Typography} from '~library/atoms/Typography';
import {DiamondIcon} from '~library/svg/DiamondIcon';
import {CheckmarkIcon} from '~library/svg/v2/CheckmarkIcon';
import {charcoal, roseWebText, white} from '~libSass/base/_exports.sass';
import * as styles from './Filter.module.scss';
import {getColor} from './utils';

interface FilterProps {
  filterSection: string;
  filterName: string;
  data: FilterType;
  isColorFilter: boolean;
  onClick: (newState: boolean) => void;
}

export const Filter: FC<FilterProps> = ({
  filterSection,
  filterName,
  data,
  isColorFilter,
  onClick,
}) => {
  const isFilterActive = useSelector((state: {filters: FiltersStoreState}) =>
    selectIsFilterActive(state, filterSection, filterName)
  );
  if (isColorFilter) {
    let color: ColorInstance;
    try {
      color = Color(getColor(filterName));
    } catch (e) {
      evite.error('Invalid color filter name:', filterName, e);
      color = Color('white');
    }
    return (
      <Button
        variant="unstyled-button"
        data-qa-id={`gallery-filter_${data.display_name}`}
        className={cx(styles.colorFilter, {
          [styles.selectedColorFilter]: isFilterActive && data.enabled,
        })}
        onClick={() => onClick(!data.on)}
        disabled={!data.enabled}
        disableProcessing
      >
        <div
          className={cx(styles.colorDotWrapper, {
            [styles.selectedColorDot]: isFilterActive && data.enabled,
          })}
        >
          <div
            className={cx(styles.colorDot, {
              [styles.whiteColorDot]: color.luminosity() > 0.95,
              [styles.disabledColorDot]: !data.enabled,
            })}
            style={{backgroundColor: color.string()}}
          >
            {data.on && <CheckmarkIcon color={color.isDark() ? white : charcoal} ratio={0.75} />}
          </div>
        </div>
        <Typography variant="paragraph3">{data.display_name}</Typography>
      </Button>
    );
  }

  return (
    <Pill
      title={data.display_name}
      startAdornment={
        filterName.toLowerCase() === 'premium' && (
          <DiamondIcon color={roseWebText} className={styles.premiumIcon} />
        )
      }
      data-qa-id={`gallery-filter_${data.display_name}`}
      className={cx(styles.filter, {[styles.selected]: isFilterActive && data.enabled})}
      onClick={() => onClick(!data.on)}
      disabled={!data.enabled}
    />
  );
};
